import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { loginSchema, type DevUser, type LoginInput } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { ApiClientError, api } from "@/lib/api";
import { useDevLogin, useLogin, useSession } from "./useSession";

type ClientConfig = { authMode: "oidc" | "dev"; appTimezone: string; unprotectedAccess: boolean };

export function LoginPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const session = useSession();
  const config = useQuery({ queryKey: ["config"], queryFn: () => api.get<ClientConfig>("/config") });

  const login = useLogin();
  const devLogin = useDevLogin();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const devUsers = useQuery({
    queryKey: ["dev-users"],
    queryFn: () => api.get<{ users: DevUser[] }>("/auth/dev-users"),
    enabled: config.data?.authMode === "dev",
    retry: false,
  });

  useDocumentTitle(t("auth.signIn"));

  if (session.data) return <Navigate to="/" replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      void navigate("/", { replace: true });
    } catch (error) {
      if (error instanceof ApiClientError && error.isValidation) {
        for (const [field, messages] of Object.entries(error.fieldErrors)) {
          form.setError(field as keyof LoginInput, { message: messages[0] });
        }
        return;
      }
      // One message for every credential failure — the API deliberately does not say
      // which part was wrong, and neither should we.
      form.setError("root", {
        message: error instanceof ApiClientError ? error.message : t("auth.signInFailed"),
      });
    }
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <main className="brand-bloom-page flex min-h-dvh w-full items-center justify-center p-4 sm:p-8">
      <div className="bg-card ring-border/60 w-full max-w-sm space-y-6 rounded-xl p-6 shadow-2xl ring-1 sm:p-8">
      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <img
            src="/infa-partner-logo.png"
            alt={t("app.logoAlt")}
            width={44}
            height={44}
            className="h-11 w-11 shrink-0 rounded-sm object-contain"
          />
          <h1 className="text-2xl font-semibold tracking-tight">{t("app.name")}</h1>
        </div>
        <p className="text-muted-foreground text-sm">{t("auth.signInLead")}</p>
      </header>

      <button
        type="button"
        onClick={() => {
          // A full navigation, not fetch: the OIDC flow is a browser redirect chain.
          window.location.href = "/api/auth/login/entra";
        }}
        className="bg-primary text-primary-foreground hover:opacity-90 focus-visible:ring-ring inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-medium transition-opacity focus-visible:ring-2 focus-visible:outline-none"
      >
        {t("auth.continueWithMicrosoft")}
      </button>

      <div className="flex items-center gap-3">
        <span className="bg-border h-px flex-1" />
        <span className="text-muted-foreground text-xs uppercase">{t("auth.or")}</span>
        <span className="bg-border h-px flex-1" />
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            {t("auth.email")}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            {...form.register("email")}
            aria-invalid={Boolean(form.formState.errors.email)}
            className="border-input bg-background focus-visible:ring-ring h-10 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
          />
          {form.formState.errors.email && (
            <p className="text-destructive text-xs">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="password" className="text-sm font-medium">
            {t("auth.password")}
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            {...form.register("password")}
            aria-invalid={Boolean(form.formState.errors.password)}
            className="border-input bg-background focus-visible:ring-ring h-10 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
          />
          {form.formState.errors.password && (
            <p className="text-destructive text-xs">{form.formState.errors.password.message}</p>
          )}
        </div>

        {rootError && (
          <p role="alert" className="text-destructive text-sm">
            {rootError}
          </p>
        )}

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="border-input hover:bg-accent focus-visible:ring-ring inline-flex h-10 w-full items-center justify-center rounded-md border text-sm font-medium disabled:opacity-50 focus-visible:ring-2 focus-visible:outline-none"
        >
          {form.formState.isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
        </button>

        <a href="/forgot-password" className="text-muted-foreground block text-center text-xs underline">
          {t("auth.forgot")}
        </a>
      </form>

      {/* Rendered only when the API reports development mode; the endpoints behind it
          answer 404 in every other environment, and the API refuses to boot with the
          mode enabled outside development. */}
      {config.data?.authMode === "dev" && (
        <section className={`space-y-3 rounded-md border p-4 ${config.data.unprotectedAccess ? "border-destructive/50 bg-destructive/10" : "border-warning/40 bg-warning/10"}`}>
          <div>
            <h2 className="text-sm font-medium">
              {config.data.unprotectedAccess ? t("auth.unprotectedTitle") : t("auth.devTitle")}
            </h2>
            <p className="text-muted-foreground text-xs">
              {config.data.unprotectedAccess ? t("auth.unprotectedDescription") : t("auth.devDescription")}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {devUsers.data?.users.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={async () => {
                  await devLogin.mutateAsync(user.id);
                  void navigate("/", { replace: true });
                }}
                className="bg-background hover:bg-accent flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm"
              >
                <span>{user.name}</span>
                <span className="text-muted-foreground text-xs">
                  {t(`role.${user.role}`)}
                </span>
              </button>
            ))}
            {devUsers.data?.users.length === 0 && (
              <p className="text-muted-foreground text-xs">
                {t("auth.noSeededUsersPrefix")} <code>pnpm db:seed</code>
                {t("auth.noSeededUsersSuffix")}
              </p>
            )}
          </div>
        </section>
      )}
      </div>
    </main>
  );
}
